import React, { useState, useRef, useCallback, useEffect } from "react";

const ScreenshotSelector = ({ onCapture, onCancel }) => {
  const [selection, setSelection] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const startRef = useRef(null);
  const overlayRef = useRef(null);
  const selectionRef = useRef(null);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest("[data-screenshot-actions]")) return;
    e.preventDefault();
    startRef.current = { x: e.clientX, y: e.clientY };
    setSelection({ x: e.clientX, y: e.clientY, w: 0, h: 0 });
    setDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragging || !startRef.current) return;
      e.preventDefault();
      const curX = Math.max(0, Math.min(e.clientX, window.innerWidth));
      const curY = Math.max(0, Math.min(e.clientY, window.innerHeight));
      const sx = startRef.current.x;
      const sy = startRef.current.y;
      setSelection({
        x: Math.min(sx, curX),
        y: Math.min(sy, curY),
        w: Math.abs(curX - sx),
        h: Math.abs(curY - sy),
      });
    },
    [dragging]
  );

  const handleMouseUp = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
  }, [dragging]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onCancel]);

  const handleConfirm = useCallback(async () => {
    const sel = selectionRef.current;
    if (!sel || sel.w < 10 || sel.h < 10) {
      onCancel();
      return;
    }

    setCapturing(true);

    try {
      // Hide overlay before capturing
      if (overlayRef.current) overlayRef.current.style.display = "none";
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      // Capture the full viewport
      const { default: html2canvas } = await import("html2canvas");
      const fullCanvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      });

      // Crop to selection using a second canvas
      const dpr = fullCanvas.width / window.innerWidth;
      const cropCanvas = document.createElement("canvas");
      cropCanvas.width = Math.round(sel.w * dpr);
      cropCanvas.height = Math.round(sel.h * dpr);
      const ctx = cropCanvas.getContext("2d");
      ctx.drawImage(
        fullCanvas,
        Math.round(sel.x * dpr),
        Math.round(sel.y * dpr),
        Math.round(sel.w * dpr),
        Math.round(sel.h * dpr),
        0,
        0,
        cropCanvas.width,
        cropCanvas.height
      );

      cropCanvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `screenshot_${Date.now()}.png`, {
              type: "image/png",
            });
            onCapture(file);
          } else {
            onCancel();
          }
        },
        "image/png",
        1.0
      );
    } catch (err) {
      console.error("Screenshot capture failed:", err);
      onCancel();
    }
  }, [onCapture, onCancel]);

  const hasSelection = selection && selection.w > 10 && selection.h > 10;

  if (capturing) return null;

  return (
    <div
      ref={overlayRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 999999,
        cursor: "crosshair",
        userSelect: "none",
      }}
    >
      {/* Semi-transparent overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0.3)",
          pointerEvents: "none",
        }}
      />

      {/* Selection rectangle (clear cutout) */}
      {selection && selection.w > 0 && selection.h > 0 && (
        <div
          style={{
            position: "absolute",
            left: selection.x,
            top: selection.y,
            width: selection.w,
            height: selection.h,
            background: "transparent",
            border: "2px solid #6366f1",
            borderRadius: 2,
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.3)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Size indicator */}
      {selection && selection.w > 30 && selection.h > 20 && (
        <div
          style={{
            position: "absolute",
            left: selection.x,
            top: selection.y - 24,
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            fontSize: 11,
            padding: "2px 6px",
            borderRadius: 3,
            pointerEvents: "none",
            fontFamily: "monospace",
          }}
        >
          {Math.round(selection.w)} x {Math.round(selection.h)}
        </div>
      )}

      {/* Instructions */}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0, 0, 0, 0.75)",
          color: "#fff",
          padding: "8px 20px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 500,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        Drag to select area &middot; Press Esc to cancel
      </div>

      {/* Confirm / Cancel buttons */}
      {hasSelection && !dragging && (
        <div
          data-screenshot-actions
          style={{
            position: "absolute",
            left: selection.x + selection.w - 120,
            top: selection.y + selection.h + 8,
            display: "flex",
            gap: 6,
            zIndex: 1000000,
          }}
        >
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleConfirm}
            style={{
              padding: "5px 14px",
              background: "#6366f1",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Capture
          </button>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onCancel}
            style={{
              padding: "5px 14px",
              background: "rgba(255,255,255,0.9)",
              color: "#333",
              border: "none",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ScreenshotSelector;
